import { useRef, useState } from "react";
import SectionCard from "./SectionCard";

type Props = {
  onSaved: (msg: string) => void;
};

const presetColors = [
  { name: "Olive", value: "#6F7A3F" },
  { name: "Terracotta", value: "#C66A4A" },
  { name: "Sage", value: "#8FA68A" },
  { name: "Ink", value: "#1F2421" },
  { name: "Sunbaked", value: "#D89B5E" },
  { name: "Plum", value: "#6E4660" },
];

const voiceTones = ["Warm & friendly", "Polished & minimal", "Playful & bold"];

export default function BrandingSection({ onSaved }: Props) {
  const [logo, setLogo] = useState<string | null>(null);
  const [primary, setPrimary] = useState(presetColors[0].value);
  const [tagline, setTagline] = useState("Effortless, gentle cleaning for the homes you love.");
  const [voice, setVoice] = useState(voiceTones[0]);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === "string") setLogo(result);
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  return (
    <div className="space-y-5">
      <SectionCard
        title="Logo"
        description="Square format works best. Used in the navbar and on receipts."
        footer={
          <button
            onClick={() => onSaved("Logo updated")}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium bg-primary-500 hover:bg-primary-600 text-background-50 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-check-line"></i>
            Save logo
          </button>
        }
      >
        <div className="flex items-center gap-5">
          <div className="w-20 h-20 rounded-2xl bg-background-100 border border-background-200 flex items-center justify-center overflow-hidden flex-shrink-0">
            {logo ? (
              <img src={logo} alt="Logo" className="w-full h-full object-cover" />
            ) : (
              <span className="w-12 h-12 flex items-center justify-center rounded-full bg-foreground-950 text-background-50">
                <i className="ri-leaf-line text-xl"></i>
              </span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => fileRef.current?.click()}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-background-100 hover:bg-background-200 text-foreground-900 text-xs font-medium cursor-pointer whitespace-nowrap"
              >
                <i className="ri-upload-2-line"></i>
                {logo ? "Replace" : "Upload"}
              </button>
              {logo && (
                <button
                  onClick={() => setLogo(null)}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-foreground-700 hover:bg-background-100 text-xs font-medium cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-delete-bin-line"></i>
                  Remove
                </button>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                onChange={handleUpload}
                className="hidden"
              />
            </div>
            <p className="text-[10px] text-foreground-500 mt-1.5">
              PNG, JPG, or SVG. Up to 5MB.
            </p>
          </div>
        </div>
      </SectionCard>

      <SectionCard
        title="Primary color"
        description="Used for CTAs, links, and key highlights."
        footer={
          <button
            onClick={() => onSaved("Brand color updated")}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium bg-primary-500 hover:bg-primary-600 text-background-50 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-check-line"></i>
            Save color
          </button>
        }
      >
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
          {presetColors.map((c) => (
            <button
              key={c.value}
              onClick={() => setPrimary(c.value)}
              className={`flex flex-col items-center gap-1.5 p-2 rounded-lg border-2 cursor-pointer transition-colors ${
                primary === c.value
                  ? "border-foreground-950"
                  : "border-transparent hover:border-background-300"
              }`}
            >
              <span
                className="w-12 h-12 rounded-full"
                style={{ backgroundColor: c.value }}
              ></span>
              <span className="text-[11px] text-foreground-700">{c.name}</span>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3 mt-4 pt-4 border-t border-background-200">
          <label className="text-xs text-foreground-700">Custom hex</label>
          <input
            type="text"
            value={primary}
            onChange={(e) => setPrimary(e.target.value)}
            className="bg-background-100 text-sm text-foreground-900 rounded-md px-3 py-2 outline-none focus:bg-background-50 focus:ring-2 focus:ring-primary-300 w-32"
          />
          <span
            className="w-8 h-8 rounded-md border border-background-200"
            style={{ backgroundColor: primary }}
          ></span>
        </div>
      </SectionCard>

      <SectionCard
        title="Voice & tagline"
        description="Sets the tone for emails, receipts, and storefront copy."
        footer={
          <button
            onClick={() => onSaved("Voice settings saved")}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium bg-primary-500 hover:bg-primary-600 text-background-50 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-check-line"></i>
            Save
          </button>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-foreground-700 mb-1.5">
              Tagline
            </label>
            <input
              type="text"
              value={tagline}
              onChange={(e) => setTagline(e.target.value)}
              className="w-full bg-background-100 text-sm text-foreground-900 rounded-md px-3 py-2.5 outline-none focus:bg-background-50 focus:ring-2 focus:ring-primary-300"
            />
            <p className="text-[10px] text-foreground-500 mt-1.5">
              Shown on the homepage hero. Keep it under 80 characters.
            </p>
          </div>
          <div>
            <span className="block text-xs font-medium text-foreground-700 mb-1.5">
              Tone of voice
            </span>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {voiceTones.map((t) => (
                <button
                  key={t}
                  onClick={() => setVoice(t)}
                  className={`px-3 py-2.5 rounded-md border text-xs text-left cursor-pointer transition-colors ${
                    voice === t
                      ? "border-primary-500 bg-primary-50 text-foreground-950"
                      : "border-background-200 text-foreground-700 hover:border-background-300"
                  }`}
                >
                  <span className="block font-semibold">{t}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </SectionCard>
    </div>
  );
}